import {useEffect, useMemo, useState} from "react";
import {ArrowDown, ArrowUp, Plus, Trash} from "lucide-react";
import {useAuth} from "@/context/use-auth";
import {useAdminTranslation} from "@/i18n/hooks";
import {useToast} from "@/hooks/use-toast";
import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {Input} from "@/components/tailwind/input";
import {Text} from "@/components/tailwind/text";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "@/components/tailwind/table";
import DeleteConfirmation, {useDeleteConfirmation} from "@/components/generic-delete-confirmation";
import {formatDateTimeString} from "@/lib/utils";
import {
    AdminPasswordPolicyResponse,
    addPasswordBlocklistEntry,
    deletePasswordBlocklistEntry,
    getAdminPasswordPolicy,
    listPasswordBlocklist,
    PasswordBlocklistEntry,
} from "@/services/password-policy-services";
import {ProblemDetail} from "@/types/problem-details";
import log from "@/lib/logger";

type SortColumn = "word" | "addedAt";
type SortDirection = "asc" | "desc";

function isDuplicateError(error: unknown): boolean {
    // The only 409 the blocklist POST endpoint raises is the duplicate-word conflict; it carries no
    // machine-readable code, so match on the status alone.
    return (error as ProblemDetail | null)?.status === 409;
}

export default function SectionPasswordBlocklist() {
    const {token} = useAuth();
    const {t} = useAdminTranslation();
    const {toast} = useToast();
    const {
        openDeleteConfirmationDialog,
        closeDeleteConfirmationDialog,
        deleteConfirmationDialogProps,
    } = useDeleteConfirmation();

    const [policy, setPolicy] = useState<AdminPasswordPolicyResponse | null>(null);
    const [entries, setEntries] = useState<PasswordBlocklistEntry[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newWord, setNewWord] = useState("");
    const [isAdding, setIsAdding] = useState(false);
    const [sortColumn, setSortColumn] = useState<SortColumn>("word");
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

    useEffect(() => {
        if (!token) {
            setIsLoading(false);
            setPolicy(null);
            setEntries([]);
            return;
        }
        let cancelled = false;
        setIsLoading(true);
        Promise.all([getAdminPasswordPolicy(token), listPasswordBlocklist(token)])
            .then(([policyData, entryData]) => {
                if (cancelled) return;
                if (policyData) setPolicy(policyData);
                if (entryData) setEntries(entryData);
            })
            .catch((error) => {
                log.error("Error fetching password blocklist data:", error);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const sortedEntries = useMemo(() => {
        const factor = sortDirection === "asc" ? 1 : -1;
        return [...entries].sort((a, b) => {
            if (sortColumn === "word") {
                return a.word.localeCompare(b.word) * factor;
            }
            return a.addedAt.localeCompare(b.addedAt) * factor;
        });
    }, [entries, sortColumn, sortDirection]);

    function toggleSort(column: SortColumn) {
        if (column === sortColumn) {
            setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    }

    async function handleAdd() {
        const normalized = newWord.trim().toLowerCase();
        // Keyboard submission (Enter) bypasses the disabled add button, so gate on the same conditions
        // here to stop a second in-flight POST that would surface a spurious duplicate-conflict toast.
        if (normalized === "" || isAdding) return;
        setIsAdding(true);
        try {
            const created = await addPasswordBlocklistEntry(token, normalized);
            if (created) {
                setEntries((prev) => [...prev, created]);
            }
            setNewWord("");
            toast({
                title: t("adminSettings.passwordBlocklist.toast.addSuccess.title"),
                description: t("adminSettings.passwordBlocklist.toast.addSuccess.description"),
            });
        } catch (error: unknown) {
            log.error("Error adding password blocklist entry:", error);
            if (isDuplicateError(error)) {
                toast({
                    title: t("adminSettings.passwordBlocklist.toast.duplicate.title"),
                    description: t("adminSettings.passwordBlocklist.toast.duplicate.description"),
                    variant: "destructive",
                });
            } else {
                toast({
                    title: t("adminSettings.passwordBlocklist.toast.addError.title"),
                    description: t("adminSettings.passwordBlocklist.toast.addError.description"),
                    variant: "destructive",
                });
            }
        } finally {
            setIsAdding(false);
        }
    }

    async function handleConfirmDelete(entityId: string | undefined) {
        if (!entityId) return;
        try {
            await deletePasswordBlocklistEntry(token, entityId);
            setEntries((prev) => prev.filter((entry) => entry.id !== entityId));
            toast({
                title: t("adminSettings.passwordBlocklist.toast.deleteSuccess.title"),
                description: t("adminSettings.passwordBlocklist.toast.deleteSuccess.description"),
            });
        } catch (error: unknown) {
            log.error("Error deleting password blocklist entry:", error);
            toast({
                title: t("adminSettings.passwordBlocklist.toast.deleteError.title"),
                description: t("adminSettings.passwordBlocklist.toast.deleteError.description"),
                variant: "destructive",
            });
        }
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <CardTitle className="text-xl font-bold">
                    {t("adminSettings.passwordBlocklist.title")}
                </CardTitle>
                <Text className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {t("adminSettings.passwordBlocklist.description")}
                </Text>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div
                        className="text-sm text-gray-500 dark:text-gray-400 py-2"
                        data-testid="password-blocklist-loading"
                    >
                        {t("adminSettings.passwordBlocklist.loading")}
                    </div>
                ) : (
                    <div className="space-y-6">
                        <div
                            className="rounded-md border-l-4 border-blue-400 dark:border-blue-500 bg-blue-50 dark:bg-blue-950 p-3 text-sm text-blue-800 dark:text-blue-200"
                            data-testid="password-blocklist-bundled-info"
                        >
                            {policy?.bundledBlocklistEnabled === false
                                ? t("adminSettings.passwordBlocklist.bundledInfoDisabled")
                                : t("adminSettings.passwordBlocklist.bundledInfo")}
                        </div>

                        <div className="flex gap-2 items-start">
                            <Input
                                value={newWord}
                                onChange={(e) => setNewWord(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        handleAdd();
                                    }
                                }}
                                placeholder={t("adminSettings.passwordBlocklist.add.placeholder")}
                                aria-label={t("adminSettings.passwordBlocklist.add.ariaLabel")}
                                data-testid="password-blocklist-word-input"
                            />
                            <Button
                                onClick={handleAdd}
                                disabled={isAdding || newWord.trim() === ""}
                                className="flex items-center whitespace-nowrap"
                                data-testid="password-blocklist-add-button"
                            >
                                <Plus className="h-4 w-4 mr-1"/>
                                {isAdding
                                    ? t("adminSettings.passwordBlocklist.add.adding")
                                    : t("adminSettings.passwordBlocklist.add.button")}
                            </Button>
                        </div>

                        {sortedEntries.length === 0 ? (
                            <Text
                                className="text-sm text-gray-500 dark:text-gray-400"
                                data-testid="password-blocklist-empty"
                            >
                                {t("adminSettings.passwordBlocklist.table.empty")}
                            </Text>
                        ) : (
                            <Table dense data-testid="password-blocklist-table">
                                <TableHead>
                                    <TableRow>
                                        <TableHeader>
                                            <SortButton
                                                label={t("adminSettings.passwordBlocklist.table.word")}
                                                ariaLabel={t("adminSettings.passwordBlocklist.sort.byWord")}
                                                active={sortColumn === "word"}
                                                direction={sortDirection}
                                                onClick={() => toggleSort("word")}
                                                testid="password-blocklist-sort-word"
                                            />
                                        </TableHeader>
                                        <TableHeader>
                                            <SortButton
                                                label={t("adminSettings.passwordBlocklist.table.addedAt")}
                                                ariaLabel={t("adminSettings.passwordBlocklist.sort.byAddedAt")}
                                                active={sortColumn === "addedAt"}
                                                direction={sortDirection}
                                                onClick={() => toggleSort("addedAt")}
                                                testid="password-blocklist-sort-added-at"
                                            />
                                        </TableHeader>
                                        <TableHeader className="text-right">
                                            <span className="sr-only">
                                                {t("adminSettings.passwordBlocklist.table.actions")}
                                            </span>
                                        </TableHeader>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sortedEntries.map((entry) => (
                                        <TableRow key={entry.id} data-testid={`password-blocklist-row-${entry.id}`}>
                                            <TableCell
                                                className="font-medium"
                                                data-testid={`password-blocklist-word-${entry.id}`}
                                            >
                                                {entry.word}
                                            </TableCell>
                                            <TableCell className="text-gray-500 dark:text-gray-400">
                                                {formatDateTimeString(entry.addedAt)}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openDeleteConfirmationDialog(
                                                        t("adminSettings.passwordBlocklist.delete.confirm", {
                                                            word: entry.word,
                                                        }),
                                                        entry.id,
                                                    )}
                                                    aria-label={t("adminSettings.passwordBlocklist.delete.ariaLabel", {
                                                        word: entry.word,
                                                    })}
                                                    data-testid={`password-blocklist-delete-${entry.id}`}
                                                >
                                                    <Trash className="h-4 w-4 text-zinc-500 dark:text-zinc-400 hover:text-red-500 dark:hover:text-red-400"/>
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                )}

                <DeleteConfirmation
                    {...deleteConfirmationDialogProps}
                    onConfirm={handleConfirmDelete}
                    onCancel={closeDeleteConfirmationDialog}
                />
            </CardContent>
        </Card>
    );
}

interface SortButtonProps {
    label: string;
    ariaLabel: string;
    active: boolean;
    direction: SortDirection;
    onClick: () => void;
    testid: string;
}

function SortButton({label, ariaLabel, active, direction, onClick, testid}: SortButtonProps) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={ariaLabel}
            className="inline-flex items-center gap-1 font-medium hover:text-zinc-700 dark:hover:text-zinc-200"
            data-testid={testid}
            data-sort-active={active || undefined}
            data-sort-direction={active ? direction : undefined}
        >
            {label}
            {active && (direction === "asc"
                ? <ArrowUp className="h-3.5 w-3.5"/>
                : <ArrowDown className="h-3.5 w-3.5"/>)}
        </button>
    );
}
